import React from "react";
import {
  Avatar,
  Box,
  Button,
  Chip,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import LockOpenOutlinedIcon from "@mui/icons-material/LockOpenOutlined";
import VideocamOutlinedIcon from "@mui/icons-material/VideocamOutlined";
import ForumOutlinedIcon from "@mui/icons-material/ForumOutlined";
import EventBusyOutlinedIcon from "@mui/icons-material/EventBusyOutlined";
import FreeBreakfastOutlinedIcon from "@mui/icons-material/FreeBreakfastOutlined";
import { isCafeHuddleTitle } from "../pages/PublicVideo/voFloorLiving";

function initialsFromName(name) {
  const s = String(name || "").trim();
  if (!s) return "?";
  const parts = s.split(/\s+/).filter(Boolean);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

export function VirtualOfficeHuddleCard({
  huddle,
  canAct,
  joining,
  requesting,
  onJoin,
  onRequest,
  tone = "light",
}) {
  const isPrivate = huddle.visibility === "private" || huddle.isPrivate;
  const isCafe = isCafeHuddleTitle(huddle.title);
  const pending = Boolean(huddle.myPendingRequestId);
  const dark = tone === "dark";
  return (
    <Paper
      variant="outlined"
      sx={{
        p: 1.5,
        borderRadius: 2,
        height: "100%",
        borderColor: (th) =>
          isCafe
            ? alpha("#d4a574", dark ? 0.7 : 0.55)
            : isPrivate
              ? alpha(th.palette.warning.main, 0.45)
              : alpha(th.palette.success.main, 0.45),
        bgcolor: (th) =>
          isCafe
            ? dark
              ? "rgba(110, 72, 36, 0.42)"
              : alpha("#d4a574", 0.16)
            : dark
              ? alpha(isPrivate ? th.palette.warning.main : th.palette.success.main, 0.16)
              : alpha(isPrivate ? th.palette.warning.main : th.palette.success.main, th.palette.mode === "dark" ? 0.12 : 0.06),
        color: dark ? "#e8eaed" : "inherit",
      }}
    >
      <Stack spacing={1.25}>
        <Stack direction="row" spacing={1} alignItems="center">
          {isCafe ? (
            <FreeBreakfastOutlinedIcon fontSize="small" sx={{ color: dark ? "#f6d7b0" : "#8a6238" }} />
          ) : isPrivate ? (
            <LockOutlinedIcon fontSize="small" sx={dark ? { color: "#fdd663" } : undefined} />
          ) : (
            <LockOpenOutlinedIcon fontSize="small" color="success" />
          )}
          <Typography fontWeight={800} noWrap sx={{ flex: 1 }} title={huddle.title}>
            {huddle.title}
          </Typography>
          <Chip
            size="small"
            color={isPrivate ? "warning" : "success"}
            label={isPrivate ? "Privada" : "Pública"}
            sx={{ fontWeight: 700 }}
          />
        </Stack>
        <Stack direction="row" spacing={0.75} alignItems="center">
          {(huddle.participants || []).slice(0, 6).map((p) => (
            <Tooltip key={p.userId} title={p.userName || `Usuário ${p.userId}`}>
              <Avatar src={p.userPhoto || undefined} sx={{ width: 28, height: 28, fontSize: "0.75rem" }}>
                {initialsFromName(p.userName || `U${p.userId}`)}
              </Avatar>
            </Tooltip>
          ))}
          {(huddle.participants || []).length > 6 ? (
            <Typography variant="caption" sx={{ color: dark ? "#bdc1c6" : "text.secondary" }}>
              +{huddle.participants.length - 6}
            </Typography>
          ) : null}
          {!(huddle.participants || []).length ? (
            <Typography variant="caption" sx={{ color: dark ? "#bdc1c6" : "text.secondary" }}>
              Sem ocupantes listados
            </Typography>
          ) : (
            <Typography variant="caption" sx={{ color: dark ? "#bdc1c6" : "text.secondary" }}>
              {huddle.participants.length} na sala
            </Typography>
          )}
        </Stack>
        {canAct ? (
          huddle.canJoinDirect ? (
            <Button
              size="small"
              variant="contained"
              startIcon={<VideocamOutlinedIcon />}
              disabled={joining}
              onClick={() => onJoin?.(huddle)}
            >
              {joining ? "Entrando…" : "Entrar"}
            </Button>
          ) : pending ? (
            <Button size="small" variant="outlined" disabled sx={dark ? { color: "#e8eaed", borderColor: "rgba(255,255,255,0.28)" } : undefined}>
              Aguardando liberação
            </Button>
          ) : (
            <Button
              size="small"
              variant="outlined"
              startIcon={<ForumOutlinedIcon />}
              disabled={requesting}
              onClick={() => onRequest?.(huddle)}
              sx={dark ? { color: "#e8eaed", borderColor: "rgba(255,255,255,0.28)" } : undefined}
            >
              {requesting ? "Enviando…" : "Pedir para entrar"}
            </Button>
          )
        ) : (
          <Typography variant="caption" sx={{ color: dark ? "#bdc1c6" : "text.secondary" }}>
            Ingresse no escritório para entrar nesta sala.
          </Typography>
        )}
      </Stack>
    </Paper>
  );
}

export function VirtualOfficeHuddleGrid({
  huddles,
  canAct,
  actingId,
  onJoin,
  onRequest,
  onCreate,
  tone = "light",
  emptyHint = "",
  compact = false,
}) {
  const dark = tone === "dark";
  const list = Array.isArray(huddles) ? huddles : [];
  return (
    <Box sx={{ width: "100%", textAlign: "left" }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: compact ? 0.75 : 1 }}>
        <Typography
          variant="subtitle2"
          fontWeight={800}
          sx={{ color: dark ? "#e8eaed" : "inherit", fontSize: compact ? "0.78rem" : undefined }}
        >
          Salas abertas
        </Typography>
        {onCreate ? (
          <Button
            size="small"
            onClick={onCreate}
            sx={
              dark
                ? { textTransform: "none", fontWeight: 700, color: "#e8eaed" }
                : { textTransform: "none", fontWeight: 700 }
            }
          >
            Nova sala
          </Button>
        ) : null}
      </Stack>
      {list.length ? (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: "repeat(auto-fill, minmax(240px, 1fr))",
            },
            gap: 1.25,
          }}
        >
          {list.map((huddle) => (
            <VirtualOfficeHuddleCard
              key={huddle.id || huddle.videoRoomId}
              huddle={huddle}
              canAct={canAct}
              joining={actingId === (huddle.videoRoomId || huddle.id) && huddle.canJoinDirect}
              requesting={actingId === (huddle.videoRoomId || huddle.id) && !huddle.canJoinDirect}
              onJoin={onJoin}
              onRequest={onRequest}
              tone={tone}
            />
          ))}
        </Box>
      ) : emptyHint ? (
        <Typography variant="body2" sx={{ color: dark ? "#bdc1c6" : "text.secondary" }}>
          {emptyHint}
        </Typography>
      ) : null}
    </Box>
  );
}

export function VirtualOfficeOtherCallsList({ people, tone = "light", compact = false }) {
  const dark = tone === "dark";
  const list = Array.isArray(people) ? people : [];
  if (!list.length) return null;
  return (
    <Box sx={{ width: "100%", textAlign: "left" }}>
      <Typography
        variant="subtitle2"
        fontWeight={800}
        sx={{ mb: compact ? 0.75 : 1, color: dark ? "#e8eaed" : "inherit", fontSize: compact ? "0.78rem" : undefined }}
      >
        Em outra chamada
      </Typography>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: compact
            ? "1fr"
            : {
                xs: "1fr",
                sm: "repeat(auto-fill, minmax(240px, 1fr))",
              },
          gap: compact ? 0.75 : 1.25,
        }}
      >
        {list.map((person) => {
          const uid = Number(person.userId);
          const name = person.userName || `Usuário ${uid}`;
          const title = person.otherMeetingTitle || "Outra reunião";
          if (compact) {
            return (
              <Stack
                key={person.id || uid}
                direction="row"
                spacing={1}
                alignItems="center"
                sx={{ minWidth: 0 }}
              >
                <Avatar
                  src={person.userPhoto || undefined}
                  sx={{ width: 28, height: 28, fontSize: "0.7rem" }}
                >
                  {initialsFromName(name)}
                </Avatar>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography fontWeight={800} noWrap title={name} sx={{ fontSize: "0.85rem" }}>
                    {name}
                  </Typography>
                  <Typography
                    variant="caption"
                    noWrap
                    display="block"
                    sx={{ color: dark ? "#bdc1c6" : "text.secondary" }}
                  >
                    Em reunião · {title}
                  </Typography>
                </Box>
              </Stack>
            );
          }
          return (
            <Paper
              key={person.id || uid}
              variant="outlined"
              sx={{
                p: 1.5,
                borderRadius: 2,
                borderColor: (th) => alpha(th.palette.warning.main, 0.45),
                bgcolor: (th) =>
                  dark
                    ? alpha(th.palette.warning.main, 0.16)
                    : alpha(th.palette.warning.main, th.palette.mode === "dark" ? 0.12 : 0.06),
                color: dark ? "#e8eaed" : "inherit",
              }}
            >
              <Stack spacing={1}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Avatar
                    src={person.userPhoto || undefined}
                    sx={{ width: 32, height: 32, fontSize: "0.75rem" }}
                  >
                    {initialsFromName(name)}
                  </Avatar>
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography fontWeight={800} noWrap title={name}>
                      {name}
                    </Typography>
                    <Typography
                      variant="caption"
                      noWrap
                      display="block"
                      sx={{ color: dark ? "#bdc1c6" : "text.secondary" }}
                    >
                      {title}
                    </Typography>
                  </Box>
                </Stack>
                <Chip
                  size="small"
                  color="warning"
                  icon={<EventBusyOutlinedIcon sx={{ "&&": { fontSize: 14 } }} />}
                  label={`Em reunião: ${title}`}
                  sx={{
                    fontWeight: 600,
                    maxWidth: "100%",
                    alignSelf: "flex-start",
                    "& .MuiChip-label": { overflow: "hidden", textOverflow: "ellipsis" },
                  }}
                />
              </Stack>
            </Paper>
          );
        })}
      </Box>
    </Box>
  );
}
